import { isNullOrUndefinedOrEmpty } from 'rewire-common';
import { observable, freeze }       from 'rewire-core';
import {
  ISelectableEntity,
}                                   from '../DataTypes';
import PagingModel                  from '../settings/PagingModel';

interface IEntityListState {
  selectedEntity?: ISelectableEntity;
  entities:        ISelectableEntity[];
  pagingModel:     PagingModel;
  currentPage?:    number;
}

export abstract class EntityListModel<TState = unknown> {
  state: IEntityListState & TState;

  constructor() {
    this.state = observable({
      selectedEntity: undefined,
      entities:       [] as ISelectableEntity[],
      pagingModel:    new PagingModel(this._load),
      currentPage:    undefined
    }) as unknown as IEntityListState & TState;
  }

  reset() {
    freeze(() => {
      this.entities.set([]);
      this.selectedEntity = undefined;
    });
  }

  protected shouldLoad(_args?: Record<string, unknown>): boolean {
    return true;
  }

  async load(selectedEntityId?: string, otherArgs?: Record<string, unknown>) {
    if (this.shouldLoad(otherArgs)) {
      await this.pagingModel.load(this.shouldMaintainCurrentPage(otherArgs));
    }

    let selectedEntity: ISelectableEntity | undefined;
    if (selectedEntityId) {
      selectedEntity = this.getEntity(selectedEntityId);
      if (!selectedEntity) {
        selectedEntity = await this.loadItem(selectedEntityId, otherArgs);
      }
    }
    this.select(selectedEntity);
  }

  _load = async (): Promise<{ cursor?: string, totalCount: number }> => {
    const results = await this._pagingLoad();
    this.updateEntities(results.data);
    return results;
  };

  get currentPage(): number | undefined {
    return this.state.currentPage;
  }

  set currentPage(value: number | undefined) {
    this.state.currentPage = value;
  }

  // override in subclass if necessary
  protected shouldMaintainCurrentPage(_args?: Record<string, unknown>): boolean {
    return false;
  }

  protected abstract _pagingLoad(): Promise<{ data?: ISelectableEntity[], cursor?: string, totalCount: number }>

  abstract loadItem(selectedEntityId: string, otherArgs?: Record<string, unknown>): Promise<ISelectableEntity | undefined>

  getEntityDisplayName(entity: ISelectableEntity) {
    return entity.id;
  }

  get pagingModel() {
    return this.state.pagingModel;
  }

  get entities(): ISelectableEntity[] {
    return this.state.entities;
  }

  getEntity(id?: string): ISelectableEntity | undefined {
    if (isNullOrUndefinedOrEmpty(id)) return undefined;
    return this.entities.find(e => e.id === id);
  }

  get selectedEntity(): ISelectableEntity | undefined {
    return this.state.selectedEntity;
  }

  set selectedEntity(entity: ISelectableEntity | undefined) {
    this.state.selectedEntity = entity;
  }

  get entityCount(): number {
    return this.entities.length;
  }

  protected updateSelectionInList() {
    if (!this.selectedEntity) return;
    const entity: ISelectableEntity | undefined = this.getEntity(this.selectedEntity.id);
    if (!entity) {
      this.selectedEntity = undefined;
    } else {
      entity.isSelected = true;
    }
  }

  protected updateEntities(entities?: ISelectableEntity[]) {
    if (entities) {
      entities.forEach(e => e.isSelected = false);
      this.entities.splice(0, this.entities.length, ...entities);
    } else {
      this.entities.splice(0, this.entities.length);
    }
    this.updateSelectionInList();
  }

  select(e?: ISelectableEntity) {
    if (this.selectedEntity !== e) {
      this.performSelection(e);
    }
  }

  protected performSelection(e?: ISelectableEntity) {
    this.entities.forEach(e => e.isSelected = false);
    this.selectedEntity = e;
    if (e) e.isSelected = true;
  }

  selectFirst() {
    this.moveHome();
  }

  moveHome(): ISelectableEntity {
    const entity = this.entities[0];
    this.select(entity);
    return entity;
  }

  moveEnd(): ISelectableEntity {
    const entity = this.entities[this.entities.length - 1];
    this.select(entity);
    return entity;
  }

  moveNext(): ISelectableEntity {
    let idx    = this.entities.indexOf(this.selectedEntity!);
    idx        = (idx === this.entities.length - 1) ? -1 : idx;
    const entity = this.entities[idx + 1];
    this.select(entity);
    return entity;
  }

  movePrevious(): ISelectableEntity {
    let idx    = this.entities.indexOf(this.selectedEntity!);
    idx        = (idx <= 0) ? this.entities.length : idx;
    const entity = this.entities[idx - 1];
    this.select(entity);
    return entity;
  }
}

export default EntityListModel;
